/**
 * Domain types as the web client sees them. These are the response shapes the
 * real API has to deliver (see DESIGN.md, "API-Backlog"); the demo store
 * produces exactly the same shapes, so swapping the source changes no screen.
 */
import type {
  AircraftClass,
  BillingLine,
  BillingTotals,
  AircraftView,
  BaseView,
  DamageCategory,
  DeclaredFleet,
  Offer,
  PaymentInterval,
  SignupCountry,
  DamageStatus,
  MemberParticipation,
  MemberRole,
  NoticeLevel,
  ReviewAction,
} from '@fsv/core';

export interface PersonRef {
  id: string;
  name: string;
  /**
   * The person's profile picture (0014): a read SAS from the API, a data URL
   * in the demo, null without one. Optional: older answers and snapshots carry none.
   */
  avatarUrl?: string | null;
}

/** The e-mail code only; the sign-in through Vereinsflieger was removed on 06.10.2026 (ADR 0002). */
export type SignInProvider = 'email-otp';

export interface Member {
  id: string;
  email: string;
  displayName: string;
  /** See PersonRef.avatarUrl. */
  avatarUrl?: string | null;
  role: MemberRole;
  isActive: boolean;
  lastSignInAt: string | null;
  createdAt: string;
  providers: SignInProvider[];
  /**
   * The invitation mail: waiting (an import sends in steps, the server's
   * invitationTimer), sent, or given up. Absent or null: none asked for. An
   * older API sends no field.
   */
  invitation?: MemberInvitation | null | undefined;
  /**
   * Further sign-in addresses (0023), oldest first; only while the membership
   * here is active. An older API sends no field.
   */
  additionalEmails?: MemberEmail[] | undefined;
}

/** A further sign-in address: added by a club admin (`club`) or confirmed by the person (`self`). */
export interface MemberEmail {
  id: string;
  email: string;
  source: 'club' | 'self';
  createdAt: string;
}

/** GET /api/me/emails: the person's own addresses and the one waiting for its code. */
export interface MyEmails {
  /** The first address: invitations and notices go there. */
  primary: string;
  additional: MemberEmail[];
  pending: { email: string; expiresAt: string } | null;
}

export interface MemberInvitation {
  state: 'pending' | 'sent' | 'failed';
  /** When the state last changed. */
  at: string;
}

export interface Drawing {
  url: string;
  width: number;
  height: number;
  /** True when the built-in silhouette stands in for a missing drawing. */
  placeholder: boolean;
}

export interface AircraftType {
  id: string;
  code: string;
  name: string;
  /**
   * The three base views always; a club's own image for a derived view
   * (unten, rechts, hinten) as an extra key — that view is then no longer in
   * `mirrorRules` (docs/api.md, "Risszeichnungen").
   */
  drawings: Record<BaseView, Drawing> & Partial<Record<AircraftView, Drawing>>;
  mirrorRules: Partial<Record<AircraftView, BaseView>>;
  credit: string;
  /** 0025: what the monthly fee counts. null = unknown (a club's own type from before); never guessed. */
  aircraftClass: AircraftClass | null;
}

export interface Aircraft {
  id: string;
  registration: string;
  typeId: string;
  typeCode: string;
  typeName: string;
  isActive: boolean;
  sortOrder: number;
}

// --- notices of the technicians (0022, @fsv/core notices.ts) -------------------

/** Somebody who confirmed „gelesen“ for the notice as it reads now. */
export interface NoticeReader {
  person: PersonRef;
  acknowledgedAt: string;
}

/**
 * A notice of the technicians on one aircraft („Kurskreisel INOP“). Members
 * read it and confirm „gelesen“; editors and admins write and resolve it.
 */
export interface AircraftNotice {
  id: string;
  aircraftId: string;
  registration: string;
  level: NoticeLevel;
  title: string;
  body: string | null;
  /** Last day shown (Europe/Berlin, YYYY-MM-DD, inclusive); null = until resolved. */
  validUntil: string | null;
  createdAt: string;
  createdBy: PersonRef | null;
  /** Last change of what a pilot reads (level, title, body); older „gelesen“ ask again. */
  revisedAt: string;
  /** Last write of any kind; sent back as `expectedUpdatedAt` by the edit form. */
  updatedAt: string;
  resolvedAt: string | null;
  resolvedBy: PersonRef | null;
  /** When the asking person confirmed this version, else null. */
  acknowledgedAt: string | null;
  /** Editors and admins only: who confirmed this version, newest first. Absent for members. */
  readers?: NoticeReader[] | undefined;
}

export interface NewNoticeInput {
  aircraftId: string;
  level: NoticeLevel;
  title: string;
  body?: string | null | undefined;
  validUntil?: string | null | undefined;
}

export interface NoticePatch {
  level?: NoticeLevel | undefined;
  title?: string | undefined;
  body?: string | null | undefined;
  validUntil?: string | null | undefined;
}

// --- fleet administration (docs/fleet-admin-ui.md) -----------------------------

export interface FleetAircraft extends Aircraft {
  /** Read only; the API never sets it. Always false in the demo store. */
  isDemo: boolean;
  deletedAt: string | null;
  /** All damages, also those in the trash. */
  damageCount: number;
  /** damageCount > 0: the registration is fixed. */
  registrationLocked: boolean;
  /** damageCount === 0 && deletedAt === null. */
  deletable: boolean;
}

export type FleetViewSource = 'bundled' | 'own' | 'mirrored' | 'missing';

export interface FleetView {
  source: FleetViewSource;
  /** For 'mirrored': the base view it is drawn from. */
  mirroredFrom: BaseView | null;
  /** Something an image view can show (also before the type is complete, for the preview), else ''. */
  url: string;
  width: number | null;
  height: number | null;
  /** Own drawing: the uuid of its upload — what the undo of a replacement sends back. */
  uploadId: string | null;
}

export interface FleetAircraftType extends AircraftType {
  scope: 'catalogue' | 'tenant';
  /** All three base views present; before that the app shows the silhouette everywhere. */
  complete: boolean;
  /** Live aircraft of the club with this type; null in write responses. */
  aircraftCount: number | null;
  /**
   * The drawings cannot be replaced: a catalogue type, or an own type whose
   * drawings ship with the API (FSV 1910's -POH types). Absent from an API
   * before 0027: then only the catalogue is locked.
   */
  drawingsLocked?: boolean;
  views: Record<AircraftView, FleetView>;
}

export interface NewAircraftInput {
  registration: string;
  typeId: string;
  sortOrder?: number | undefined;
}

export interface AircraftPatch {
  registration?: string | undefined;
  typeId?: string | undefined;
  sortOrder?: number | undefined;
  isActive?: boolean | undefined;
}

export interface NewAircraftTypeInput {
  code: string;
  name: string;
  /** Source of the drawings, required (spec 14). */
  credit: string;
  /** Required (0025): it decides the monthly fee. */
  aircraftClass: AircraftClass;
}

export interface AircraftTypePatch {
  name?: string | undefined;
  credit?: string | undefined;
  /** Set or changed, never back to unknown. */
  aircraftClass?: AircraftClass | undefined;
}

export interface DrawingFile {
  /** The PNG as chosen. Width and height are measured by the server (the demo: by core's IHDR reader), never sent. */
  blob: Blob;
}

export type DrawingWarning = 'no_transparency';

export interface DrawingUploadResult extends WriteResult<FleetAircraftType> {
  /** 'no_transparency': accepted, but without an alpha channel (spec 7 asks for a transparent background). */
  warnings: DrawingWarning[];
}

// --- club export (DSGVO Art. 20, offboarding) -----------------------------------

/** One export file; `url` is a read link valid until `expiresAt` (1 h), minted per response, '' when the file is not available. */
export interface ExportLink {
  name: string;
  size: number;
  createdAt: string;
  url: string;
  expiresAt: string;
}

export interface ExportResult extends ExportLink {
  /** Rows per table in the archive. */
  tables: Record<string, number>;
  photos: { count: number; missing: string[] };
}

export interface PinPosition {
  id: string;
  number: number;
  view: AircraftView;
  x: number;
  y: number;
  status: DamageStatus;
  /** The first of `categories` (catalogue order). */
  category: DamageCategory;
  /** All types of the damage, catalogue order (0021). */
  categories: DamageCategory[];
}

export interface FleetEntry {
  aircraft: Aircraft;
  counts: Record<DamageStatus, number>;
  openCount: number;
  lastReportAt: string | null;
  /** Open damages, all views; the fleet card draws those on the top view. */
  openPins: PinPosition[];
}

export interface Damage {
  id: string;
  number: number;
  aircraftId: string;
  registration: string;
  typeCode: string;
  /** The first of `categories` (catalogue order) — what an API before 0021 sends alone. */
  category: DamageCategory;
  /** All types of the damage, catalogue order, at least one (0021). */
  categories: DamageCategory[];
  view: AircraftView;
  x: number;
  y: number;
  title: string;
  description: string | null;
  status: DamageStatus;
  reportedBy: PersonRef;
  reportedAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  reviewedBy: PersonRef | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  mergedIntoId: string | null;
  mergedIntoNumber: number | null;
  imageCount: number;
  thumbUrl: string | null;
  /** Only admins ever see a deleted damage (detail page, trash). */
  deletedAt: string | null;
}

export interface DamageImage {
  id: string;
  damageId: string;
  url: string;
  thumbUrl: string;
  width: number;
  height: number;
  isOverview: boolean;
  /** 'video' since 0018: `url` is the MP4, `thumbUrl` its poster. Absent from older answers: a photo. */
  kind?: 'photo' | 'video';
  /** A video's length in milliseconds, as the server read it. */
  durationMs?: number | null;
  uploadedBy: PersonRef;
  createdAt: string;
}

export interface DamageComment {
  id: string;
  damageId: string;
  author: PersonRef;
  body: string;
  createdAt: string;
}

export type EntityType =
  | 'damage'
  | 'damage_image'
  | 'damage_comment'
  | 'aircraft'
  /** A club's own aircraft type (fleet admin): created, renamed, a drawing uploaded (`after.view`). */
  | 'aircraft_type'
  | 'member'
  | 'setting'
  | 'tenant'
  /** A notice of the technicians on an aircraft (0022): created, changed, resolved (`status_changed`). */
  | 'aircraft_notice';

export type EventAction =
  | 'created'
  | 'updated'
  | 'status_changed'
  | 'image_added'
  | 'image_removed'
  | 'merged_report'
  | 'deleted'
  | 'restored'
  | 'undo'
  | 'import'
  /** The club's data was exported (entity 'tenant'; `after`: file, size, tables, photos). */
  | 'exported';

export type JsonObject = Record<string, unknown>;

export interface ChangeEvent {
  id: number;
  actor: PersonRef | null;
  entityType: EntityType;
  entityId: string;
  /** Human label of the entity at the time of the event, e.g. "FSV-000042" or "D-EAMX". */
  entityLabel: string;
  /** For events on images and comments: the damage they belong to. */
  damageId: string | null;
  action: EventAction;
  before: JsonObject | null;
  after: JsonObject | null;
  reason: string | null;
  /** For action 'undo': the event that was compensated. */
  undoOf: number | null;
  createdAt: string;
}

export interface DamageDetail {
  damage: Damage;
  aircraft: Aircraft;
  images: DamageImage[];
  comments: DamageComment[];
  events: ChangeEvent[];
  /** Damages that were merged into this one. */
  mergedFrom: { id: string; number: number }[];
}

export interface NewImage {
  /**
   * The prepared JPEG (2048 px, metadata stripped by re-encoding): a data: URL
   * on the web, a file: URI on the native app. Only the platform's
   * PhotoTransport (apiSource.ts) and image views read it.
   */
  dataUrl: string;
  /** The 400 px thumbnail, same form as `dataUrl`. */
  thumbDataUrl: string;
  width: number;
  height: number;
  isOverview: boolean;
  /**
   * 'video' (stage 2, 0018): `dataUrl` is the compressed MP4 with its metadata
   * stripped (web: a blob: URL, app: a file: URI) and `thumbDataUrl` its
   * poster frame. Absent: a photo.
   */
  kind?: 'photo' | 'video';
  /** A video's length as the device read it, for the tile; the server measures again. */
  durationMs?: number;
}

export interface CreateDamageInput {
  aircraftId: string;
  view: AircraftView;
  x: number;
  y: number;
  /** One or more types (0021); the order does not matter, the server sorts. */
  categories: DamageCategory[];
  title: string;
  description: string | null;
  /** Ignored for members: their reports are always 'gemeldet'. */
  status?: DamageStatus | undefined;
  images: NewImage[];
  /**
   * Made once per report and sent with every attempt: the server answers a
   * repeat with the damage it already created (migration 0012). The demo
   * ignores it.
   */
  clientRequestId?: string | undefined;
  /**
   * How many photos/videos follow the create (the club's photo rule). Default:
   * `images.length`; the app's outbox sends the report first and its photos
   * after, so it says how many it holds.
   */
  mediaCount?: number | undefined;
}

export interface UpdateDamageInput {
  title?: string | undefined;
  description?: string | null | undefined;
  /** The full set of types (0021). */
  categories?: DamageCategory[] | undefined;
  status?: DamageStatus | undefined;
  view?: AircraftView | undefined;
  x?: number | undefined;
  y?: number | undefined;
}

export interface ReviewInput {
  action: ReviewAction;
  note?: string | undefined;
  mergeIntoId?: string | undefined;
}

export interface WriteOptions {
  /** Set when this write compensates an earlier one ("Rückgängig"); logged as action 'undo'. */
  undoOf?: number | undefined;
  /**
   * updateDamage: the `updatedAt` the form was opened with. If the damage
   * changed since, the write is refused with 409 (DataError code 'stale').
   */
  expectedUpdatedAt?: string | undefined;
}

/** Options of a write that can move pins (type change, drawing upload). */
export interface ForceOptions extends WriteOptions {
  /** After the person confirmed: apply the change although pins of existing damages would drift. */
  force?: boolean | undefined;
}

export interface WriteResult<T> {
  value: T;
  eventId: number;
  /**
   * addMember, createTenant, addTenantAdmin: whether the invitation mail went
   * out. `false` is shown to the admin; undefined means "not applicable".
   */
  delivered?: boolean | undefined;
}

/** Progress of a photo upload, one step per photo (upload-url → two PUTs → register). */
export interface UploadProgress {
  done: number;
  total: number;
  /** How far the item being sent is, 0…1, when the transport can tell (a video's bytes). */
  fraction?: number;
}

/** A photo that did not make it; the others of the same batch still do. */
export interface ImageUploadFailure {
  /** Index into the images that were passed in. */
  index: number;
  message: string;
  /**
   * DataError status when the photo failed on a request: 0 = never reached
   * the server or the storage account, 503 = storage unavailable. The native
   * outbox keeps such a photo for the next attempt instead of dropping it;
   * a refusal (400, 403) is final. Absent in the demo.
   */
  status?: number | undefined;
}

export interface UploadOptions {
  onProgress?: ((progress: UploadProgress) => void) | undefined;
}

/**
 * The API registers one photo per call, so each photo has its own change
 * event; `eventIds[i]` belongs to `value[i]` and is what its undo must name.
 * `eventId` is the first of them.
 */
export interface AddImagesResult extends WriteResult<DamageImage[]> {
  eventIds: number[];
  failures: ImageUploadFailure[];
}

/** The damage exists even when some of its photos were refused. */
export interface CreateDamageResult extends WriteResult<Damage> {
  imageFailures: ImageUploadFailure[];
}

export type PeriodDays = 30 | 90 | 365;

export interface SearchQuery {
  q: string;
  aircraft: string | null;
  category: DamageCategory | null;
  statuses: DamageStatus[];
  view: AircraftView | null;
  periodDays: PeriodDays | null;
  withPhoto: boolean;
}

export interface InboxEntry {
  damage: Damage;
  nearby: Damage[];
  ageDays: number;
  overdue: boolean;
}

export interface Settings {
  /**
   * What members of this club may write (`@fsv/core`, participation.ts):
   * `report` (default), `comment`, `read`. Editors and admins are never limited.
   */
  memberParticipation: MemberParticipation;
  duplicateRadius: number;
  reviewDueDays: number;
  /**
   * Members' reports need a photo or video (`@fsv/core`, media.ts). Optional:
   * an older API does not send it, which means off.
   */
  mediaRequired?: boolean;
  /**
   * Technik may ground an aircraft with a notice of level `gesperrt`
   * (`@fsv/core`, notices.ts). Optional: an older API does not send it, which means off.
   */
  aircraftGrounding?: boolean;
}

export interface EventFilter {
  entityType?: EntityType | undefined;
  actorId?: string | undefined;
  action?: EventAction | undefined;
  from?: string | undefined;
  to?: string | undefined;
}

/** 'aircraft': a soft-deleted aircraft, restored with restoreAircraft(). */
export type TrashKind = 'damage' | 'damage_image' | 'damage_comment' | 'aircraft';

export interface TrashItem {
  kind: TrashKind;
  id: string;
  label: string;
  context: string;
  damageId: string | null;
  deletedAt: string;
  deletedBy: PersonRef | null;
  thumbUrl: string | null;
}

export interface MemberImportRow {
  email: string;
  name: string;
  /** Absent when the file has no role column: a new person becomes a member, an existing one keeps the role. */
  role?: MemberRole | undefined;
}

export interface ImportSummary {
  added: number;
  updated: number;
  deactivated: number;
  /** Invitations waiting to be sent (import with `invite`). An older API sends none. */
  invitations?: number | undefined;
}

export interface NewMemberInput {
  email: string;
  displayName: string;
  role: MemberRole;
}

export interface MemberPatch {
  role?: MemberRole | undefined;
  isActive?: boolean | undefined;
  displayName?: string | undefined;
}

// --- tenancy (ADR 0004) ------------------------------------------------------

/** Role inside one tenant (memberships.role). Leaving a club is memberships.is_active = 0, not a role. */
export type TenantRole = 'member' | 'editor' | 'admin';

/** One active membership of the signed-in person, as GET /api/me lists it. */
export interface MembershipRef {
  tenantId: string;
  name: string;
  slug: string;
  /** Tenant code, the prefix of every damage reference shown in this club (FSV-000042). */
  code: string;
  role: TenantRole;
  /** The club's logo as a read SAS (0019) or a data URL in the demo; null or absent (older API): show the code. */
  logoUrl?: string | null;
}

/** GET /api/me. The session is cross-tenant; the role is per membership. */
export interface Me {
  memberId: string;
  email: string;
  displayName: string;
  /** From the app setting PLATFORM_MANAGER_EMAILS, never from the database. */
  isPlatformManager: boolean;
  /** The person's own profile picture, or null (initials). Optional for answers of an older API. */
  avatarUrl?: string | null;
  memberships: MembershipRef[];
}

/** Optional contact data of a club; every field may be null. */
export interface TenantContact {
  clubName: string | null;
  street: string | null;
  postalCode: string | null;
  city: string | null;
  country: string | null;
  phone: string | null;
  contactEmail: string | null;
  website: string | null;
}

export const TENANT_CONTACT_FIELDS = ['clubName', 'street', 'postalCode', 'city', 'country', 'phone', 'contactEmail', 'website'] as const satisfies readonly (keyof TenantContact)[];

/** A tenant (club) as the platform area and the club's own admins see it. */
export interface Tenant extends TenantContact {
  id: string;
  name: string;
  /** Short name in every link (/v/<slug>/…); fixed at creation, never changes. */
  slug: string;
  /** "Kürzel": 2–5 letters A–Z, prefix of damage references (FSV-000042); fixed at creation. */
  code: string;
  /** Internal note of the platform manager; not shown to the club. */
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  counts: { members: number; aircraft: number; damages: number };
}

/** GET /t/{tenantId}/contact: who is responsible for a club's data, readable by every member (privacy page). */
export interface ClubContact extends TenantContact {
  name: string;
}

export interface TenantAdminRef extends PersonRef {
  email: string;
}

/** GET /api/platform/tenants/{id}: master data plus the administrators, no business data. */
export interface TenantDetail {
  tenant: Tenant;
  admins: TenantAdminRef[];
}

export interface NewTenantInput extends Partial<TenantContact> {
  name: string;
  slug: string;
  code: string;
  notes?: string | null | undefined;
  firstAdmin: { name: string; email: string };
}

/** Platform manager: everything but the slug and the code. */
export interface TenantPatch extends Partial<TenantContact> {
  name?: string | undefined;
  notes?: string | null | undefined;
  isActive?: boolean | undefined;
}

/** Club admin: contact data only; name, notes and status belong to the platform. */
export type ClubPatch = Partial<TenantContact>;

/**
 * A club's contract (0024), as GET /api/platform/contracts answers it.
 * `status` is effective: a sign-up waiting longer than SIGNUP_PENDING_HOURS
 * reads 'expired' even before anybody wrote that down. `offer` is the fee
 * for the declared fleet, computed by the API with @fsv/core's offerFor.
 */
export interface Contract {
  id: string;
  contractNumber: string | null;
  tenantId: string | null;
  tenant: { name: string; slug: string; isActive: boolean } | null;
  status: 'pending' | 'active' | 'terminated' | 'expired';
  source: 'online' | 'manual';
  clubName: string;
  street: string;
  postalCode: string;
  city: string;
  country: SignupCountry;
  registerCourt: string | null;
  registerNumber: string | null;
  representativeName: string;
  representativeRole: string;
  contactEmail: string;
  contactPhone: string | null;
  billingName: string | null;
  billingStreet: string | null;
  billingPostalCode: string | null;
  billingCity: string | null;
  billingEmail: string | null;
  vatId: string | null;
  paymentInterval: PaymentInterval;
  founder: boolean;
  founderRank: number | null;
  declared: DeclaredFleet;
  priceListVersion: string;
  agbVersion: string;
  avvVersion: string;
  termsVersion: string;
  startsOn: string | null;
  concludedAt: string | null;
  terminatedAt: string | null;
  endsOn: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  /** The club's further areas besides `tenantId`, oldest first (0026). */
  departments: ContractDepartment[];
  offer: Offer;
}

/** A further area of a club's contract (0026): another tenant, counted and billed with the contract. */
export interface ContractDepartment {
  tenantId: string;
  name: string;
  slug: string;
  code: string;
  isActive: boolean;
  addedAt: string;
}

/** POST /api/platform/contracts: a contract for an existing club, concluded outside the form (FSV 1910). */
export interface NewContractInput {
  tenantId: string;
  clubName: string;
  street: string;
  postalCode: string;
  city: string;
  country: SignupCountry;
  registerCourt?: string;
  registerNumber?: string;
  representativeName: string;
  representativeRole: string;
  contactEmail: string;
  contactPhone?: string;
  billingName?: string;
  billingStreet?: string;
  billingPostalCode?: string;
  billingCity?: string;
  billingEmail?: string;
  vatId?: string;
  paymentInterval: PaymentInterval;
  fleet: { motor: number; tmg: number; glider: number; ul: number };
  /** true: the lowest free place; a number: exactly that one; false: none. */
  founder: boolean | number;
  /** YYYY-MM-DD */
  startsOn: string;
  /** YYYY-MM-DD; defaults to startsOn. */
  concludedOn?: string;
  notes?: string | null;
}

export interface ContractPatch {
  notes?: string | null;
  paymentInterval?: PaymentInterval;
  startsOn?: string | null;
  founder?: boolean | number;
}

/** GET /api/platform/fleet-counts?month=YYYY-MM (0025): one club's billable fleet at the 1st, 00:00 Berlin. */
export interface TenantFleetCount {
  tenantId: string;
  name: string;
  slug: string;
  code: string;
  isActive: boolean;
  counts: Record<AircraftClass, number>;
  /** Counted aircraft whose type has no class: billing asks, never guesses. */
  unclassified: number;
  unclassifiedTypes: string[];
  total: number;
}

export interface FleetCounts {
  /** YYYY-MM */
  month: string;
  /** The instant counted at, UTC ISO. */
  at: string;
  classes: AircraftClass[];
  tenants: TenantFleetCount[];
}

/** The address an invoice goes to: the billing address of the contract, else the club's. */
export interface BillTo {
  name: string;
  street: string;
  postalCode: string;
  city: string;
  country: string;
  email: string;
  vatId: string | null;
}

/** One line of GET /api/platform/billing: a club and its contract (or none) in one month. */
export interface BillingEntry extends BillingLine {
  tenantId: string;
  /** The contract's further areas counted in this line (0026). */
  departments: { tenantId: string; name: string; code: string }[];
  clubName: string;
  slug: string;
  code: string;
  clubActive: boolean;
  contractId: string | null;
  contractNumber: string | null;
  founderRank: number | null;
  startsOn: string | null;
  endsOn: string | null;
  billTo: BillTo | null;
}

/** GET /api/platform/billing?month=YYYY-MM: the month's lines and what is due (core billing.ts). */
export interface Billing {
  /** YYYY-MM */
  month: string;
  classes: AircraftClass[];
  prices: {
    poweredCents: number;
    gliderCents: number;
    ulCents: number;
    capCents: number;
    founderDiscountPercent: number;
    yearlyDiscountPercent: number;
    vatPercent: number;
  };
  lines: BillingEntry[];
  totals: BillingTotals;
}
